
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'

import { fetchRunsheetOrdersByShift } from '@/entities/orders/thunks/fetchRunsheetOrdersByShift'
import { fetchRunsheetOrdersByShifts } from '@/entities/orders/thunks/fetchRunsheetOrdersByShifts'
import { fetchUnassignedOrdersByRunsheet } from '@/entities/orders/thunks/fetchUnassignedOrdersByRunsheet'
import { selectActiveRunsheetsWithShifts } from '@/entities/schedules'
import { removeShift } from '@/entities/schedules/thunks/removeShift'
import { showShiftOnMap } from '@/entities/widgetWindows/thunks/showShiftOnMap'
import { showShiftsOnGantt } from '@/entities/widgetWindows/thunks/showShiftsOnGantt'
import { showUnassignedOnMap } from '@/entities/widgetWindows/thunks/showUnassignedOnMap'
import { useMosaicLayoutContext } from '@/fragments/MosaicLayout/MosaicLayoutContext'
import { AddRunDialog } from '@/fragments/OperationsDialogs/AddRunDialog/AddRunDialog'
import { CloseRunsheetDialog } from '@/fragments/OperationsDialogs/CloseRunsheetDialog/CloseRunsheetDialog'
import { ConfirmDialog } from '@/fragments/OperationsDialogs/ConfirmDialog/ConfirmDialog'
import { EditShiftDialog } from '@/fragments/OperationsDialogs/EditShiftDialog/EditShiftDialog'
import { UpdateShiftStartTimeDialog } from '@/fragments/OperationsDialogs/UpdateShiftStartTimeDialog/UpdateShiftStartTimeDialog'
import { CustomTree } from '@/fragments/TreeView/CustomTree'
import { CustomTreeSkeleton } from '@/fragments/TreeView/CustomTreeSkeleton'
import { HistoricalRunsheetTree } from '@/fragments/TreeView/HistoricalRunsheetTree'
import { CustomTreeItemType, RunsheetShiftTreeItemType } from '@/fragments/TreeView/model/CustomTreeItemType'
import { TreeItemAction } from '@/fragments/TreeView/model/TreeItemAction'
import {
  StyledAccordion, StyledAccordionDetails, StyledAccordionSummary, StyledActionIcon, StyledHeaderTitle, StyledLoadingContainer,
  StyledLoadingIndicator, StyledSectionHeader, StyledTreeContainer, StyledTreeHeader, StyledTreeItemIcon,
} from '@/pages/Operations/StyledTreeView'
import { Icon } from '@/shared/components'
import { Dropdown } from '@/shared/components/Dropdown/Dropdown'
import { DropdownItem } from '@/shared/components/Dropdown/DropdownItem'
import { GridTableDragType } from '@/shared/components/GridTable/models/GridTableDragType'
import { useDialogContext } from '@/shared/components/Modal/DialogContext'
import { TREE_WIDGETS } from '@/shared/components/Mosaic/models/widgetType'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { RUNSHEET_TREE_SHOW_UTILISATION_PIES_LOCAL_STORAGE_KEY } from '@/shared/config/consts'
import { useLocalStorage } from '@/shared/lib/useLocalStorage'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import {
  faArrowUpRightFromSquare, faChartPieSimple, faChevronRight, faClock, faEllipsisVertical, faEyeSlash, faLock, faPlus,
} from '@fortawesome/pro-regular-svg-icons'
import { faTruck } from '@fortawesome/pro-solid-svg-icons'
import { Box, styled, Typography } from '@mui/material'
import { TreeViewBaseItem } from '@mui/x-tree-view/models'

import { useOperationTreeState } from './libs/useOperationTreeState'

export type RunsheetTreeViewProps = {
  //
}

export const RunsheetTreeView = () => {
  const { isLoading, isSuccess, isTreeViewLoading } = useAppSelector(state => state.runsheetPage)
  const [expanded, setExpanded] = useState<string | false>('active-runsheets')
  const [isUtilisationPieShown, setUtilisationPieShown] = useLocalStorage(RUNSHEET_TREE_SHOW_UTILISATION_PIES_LOCAL_STORAGE_KEY, true)

  const allRunsheets = useAppSelector(state => selectActiveRunsheetsWithShifts(state))
  const activeGroupId = useAppSelector(state => state.userInterface.activeGroupId)

  const dispatch = useAppDispatch()
  const { treeState, setTreeState } = useOperationTreeState(activeGroupId, 'runsheet')
  const { openDialog } = useDialogContext()
  const { setToastContent } = useToastContext()
  const { closeWidgetWindows } = useMosaicLayoutContext()

  const runsheetTree: TreeViewBaseItem<CustomTreeItemType>[] = useMemo(() => {
    const tree = allRunsheets?.map(runsheet => {
      let children: TreeViewBaseItem<CustomTreeItemType>[] = []

      // Add Unassigned Orders
      const unassignedActions: TreeItemAction[] = [
        {
          type: 'actionItem',
          label: 'Show On Map',
          icon: faArrowUpRightFromSquare,
          action: () => {
            dispatch(fetchUnassignedOrdersByRunsheet({ runsheetId: runsheet.id }))
            dispatch(showUnassignedOnMap({ scheduleId: runsheet.id }))
          },
        },
      ]

      const unassignedOrders = {
        id: `${runsheet.id}-unassigned`,
        runsheetId: runsheet.id,
        level: 1,
        label: `Unassigned (${runsheet.unassignedOrderCount})`,
        widgetType: TREE_WIDGETS.RunsheetUnassignedOrders,
        isDraggable: true,
        isDroppable: false,
        acceptDropTypes: [GridTableDragType.SHIFT_STOP_ROW, GridTableDragType.UNASSIGNED_ORDER_ROW],
        actions: unassignedActions,
      }

      children.push(unassignedOrders)

      // Add Shifts
      const shifts: RunsheetShiftTreeItemType[] = runsheet.shifts.map(shift => {
        const shActions: TreeItemAction[] = [
          {
            type: 'actionItem',
            label: 'View Run',
            icon: faTruck,
            action: () => {
              openDialog(EditShiftDialog, {
                shiftId: shift.id,
                formType: 'view',
                onClose: () => {
                  // Any callback after the dialog is closed
                },
              })
            },
          },
          {
            type: 'actionItem',
            label: 'Update Start Time',
            disabled: Boolean(shift.startedOn),
            icon: faClock,
            action: () => {
              openDialog(UpdateShiftStartTimeDialog, {
                scheduleId: runsheet.id,
                shiftId: shift.id,
                formType: 'edit',
                onClose: () => {
                  // Any callback after the dialog is closed
                },
              })
            },
          },
          {
            type: 'actionItem',
            label: 'Delete Run',
            disabled: shift.startedOn !== null && shift.startedOn !== undefined,
            icon: faTrashCan,
            action: () => {
              openDialog(ConfirmDialog, {
                title: 'Delete Run',
                message: 'Are you sure you want to delete this shift?',
                onConfirm: () => {
                  dispatch(removeShift({ id: shift.id }))
                  closeWidgetWindows([`${TREE_WIDGETS.RunsheetShiftTable}-${shift.id}`])
                },
                confirmText: 'Delete',
              })
            },
          },
          {
            type: 'divider',
          },
          {
            type: 'actionItem',
            label: 'Show on Map',
            icon: faArrowUpRightFromSquare,
            action: () => {
              dispatch(fetchRunsheetOrdersByShift({ shiftId: shift.id }))
              dispatch(showShiftOnMap({ shiftId: shift.id }))
            },
          },
          {
            type: 'actionItem',
            label: 'Show on Gantt',
            icon: faArrowUpRightFromSquare,
            action: () => {
              dispatch(fetchRunsheetOrdersByShift({ shiftId: shift.id }))
              dispatch(showShiftsOnGantt({ shiftIds: [shift.id] }))
            },
          },
        ]

        return {
          widgetType: TREE_WIDGETS.RunsheetShiftTable,
          id: shift.id.toString(),
          level: 1,
          shiftId: shift.id,
          runsheetId: runsheet.id,
          label: `${shift.name} (${shift.orderCount})`,
          uiColor: shift.uiColor,
          violationCount: shift.violationCount,
          isDraggable: true,
          isDroppable: false,
          acceptDropTypes: [
            GridTableDragType.SHIFT_STOP_ROW,
            GridTableDragType.UNASSIGNED_ORDER_ROW,
          ],
          actions: shActions,
          status: shift.status,
          statusTooltip: shift.statusTooltip,

          // For Utilisation
          showUtilisationPie: isUtilisationPieShown,
          maxVolume: shift.maxVolume,
          maxWeight: shift.maxWeight,
          maxWorkingTimeInMins: shift.maxWorkingTimeInMins,
          totalWorkingTimeInMins: shift.totalWorkingTimeInMins,
          volumeCapacity: shift.volumeCapacity,
          weightCapacity: shift.weightCapacity,
          loads: shift.loads,
        }
      })

      children = children.concat(shifts)

      // Runsheet Actions
      const rsActions: TreeItemAction[] = [
        {
          type: 'actionItem',
          label: 'Add Run',
          icon: faPlus,
          action: () => {
            openDialog(AddRunDialog, {
              scheduleId: runsheet.id,
              onClose: () => {
                // Any callback after the dialog is closed
              },
            })
          },
        },
        {
          type: 'divider',
        },
        {
          type: 'actionItem',
          label: 'Show Shifts on Gantt',
          icon: faArrowUpRightFromSquare,
          action: () => {
            const runsheetShiftIds = runsheet.shifts.map(shift => shift.id)

            dispatch(fetchRunsheetOrdersByShifts({ shiftIds: runsheetShiftIds }))
            dispatch(showShiftsOnGantt({ shiftIds: runsheetShiftIds }))
          },
        },
        {
          type: 'divider',
        },
        {
          type: 'actionItem',
          label: 'Close Runsheet',
          icon: faLock,
          action: () => {
            const dayjsToday = dayjs().startOf('day')
            const dayjsRsDate = dayjs(runsheet.name, 'YYYY-MM-DD').startOf('day')

            if (dayjsRsDate.valueOf() >= dayjsToday.valueOf()) {
              setToastContent({
                open: true,
                title: 'Close Runsheet Error.',
                content: 'Runsheet for Today/Future date cannot be Closed.',
                severity: 'error',
              })

              return
            }

            openDialog(CloseRunsheetDialog, {
              runsheetId: runsheet.id,
              onClose: () => {
                // Any callback after the dialog is closed
              },
            })
          },
        },
      ]

      return {
        id: runsheet.id.toString(),
        level: 0,
        runsheetId: runsheet.id,
        label: runsheet.name,
        widgetType: TREE_WIDGETS.RunsheetSummaryTable,
        violationCount: runsheet.violationCount,
        children: children,
        isDraggable: true,
        isDroppable: false,
        acceptDropTypes: [],
        actions: rsActions,
      }
    }) || []

    return tree
  }, [dispatch, isUtilisationPieShown, allRunsheets, openDialog, setToastContent, closeWidgetWindows])

  // Handle the change of accordion panel
  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  // Handle the change of expanded items
  const handleExpandedItemsChange = (
    _: React.SyntheticEvent,
    itemIds: string[],
  ) => {
    setTreeState({
      expandedItems: itemIds,
    })
  }

  return (
    <Box>
      {isLoading && (
        <CustomTreeSkeleton />
      )}
      {!isLoading && isSuccess &&
        <>
          <StyledTreeHeader>
            <StyledSectionTypography variant="h1">
              RUNSHEET
            </StyledSectionTypography>
          </StyledTreeHeader>
          <StyledAccordion
            disableGutters
            defaultExpanded
            expanded={expanded === 'active-runsheets'}
            onChange={handleAccordionChange('active-runsheets')}
            aria-controls="active-runsheet-tree"
            id="active-runsheet-tree"
          >
            <StyledAccordionSummary
              aria-controls="active-runsheets-content"
              id="active-runsheet-header"
            >
              <StyledSectionHeader>
                <StyledHeaderTitle>
                  <StyledTreeItemIcon className="MuiSvgIcon-cheveron" icon={faChevronRight} />
                  <StyledSectionTypography variant="body1">
                    Active Runsheets
                  </StyledSectionTypography>
                </StyledHeaderTitle>
                <Box sx={{ display: 'inline-flex', flexDirection: 'row' }}>
                  <Dropdown
                    icon={<StyledActionIcon icon={faEllipsisVertical} />}
                    value="Actions"
                    isIconButton
                    slotProps={{
                      iconButton: {
                        round: true,
                        sx: { margin: 0 },
                      },
                      popper: {
                        placement: 'right-start',
                      },
                    }}
                  >
                    <DropdownItem onSelect={() => { setUtilisationPieShown(prev => !prev) }}>
                      <Icon icon={isUtilisationPieShown ? faEyeSlash : faChartPieSimple} />
                      {isUtilisationPieShown ? 'Hide' : 'Show'} Run Utilisation
                    </DropdownItem>
                  </Dropdown>
                </Box>
              </StyledSectionHeader>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <StyledTreeContainer>
                <CustomTree
                  items={runsheetTree}
                  defaultExpandedItems={treeState ? treeState.expandedItems : []}
                  onExpandedItemsChange={handleExpandedItemsChange}
                />
                {runsheetTree.length === 0 && (
                  <StyledPlaceholderText variant="body1" >
                    Publish a scenario from planning to create a runsheet.
                  </StyledPlaceholderText>
                )}
              </StyledTreeContainer>
            </StyledAccordionDetails>
          </StyledAccordion>
          <StyledAccordion
            disableGutters
            defaultExpanded
            expanded={expanded === 'historical-runsheets'}
            onChange={handleAccordionChange('historical-runsheets')}
            aria-controls="historical-runsheet-tree"
            id="historical-runsheet-tree"
          >
            <StyledAccordionSummary
              aria-controls="historical-runsheets-content"
              id="historical-runsheets"
            >
              <StyledSectionHeader>
                <StyledHeaderTitle>
                  <StyledTreeItemIcon className="MuiSvgIcon-cheveron" icon={faChevronRight} />
                  <StyledSectionTypography variant="body1">
                    Historical Runsheets
                  </StyledSectionTypography>
                </StyledHeaderTitle>
                <Box sx={{ display: 'inline-flex', flexDirection: 'row' }}>
                </Box>
              </StyledSectionHeader>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <StyledTreeContainer>
                <HistoricalRunsheetTree />
              </StyledTreeContainer>
            </StyledAccordionDetails>
          </StyledAccordion>
        </>
      }
      <StyledLoadingContainer>
        {isTreeViewLoading && <StyledLoadingIndicator size={16} />}
      </StyledLoadingContainer>
    </Box >
  )
}

const StyledSectionTypography = styled(Typography)`
  font-weight: 500;
  font-size: ${({ theme }) => theme.typography.pxToRem(14)};
  padding: ${({ theme }) => theme.typography.pxToRem(8)};
`

const StyledPlaceholderText = styled(Typography)`
  padding: ${props => props.theme.typography.pxToRem(16)} ${props => props.theme.typography.pxToRem(8)};
  color: ${props => props.theme.palette.text.secondary};
  text-align: center;
`
